/**
 * Hands-free TTS text filter — a rolling-buffer state machine that turns the
 * raw reply stream into speakable prose.
 *
 * Why a state machine and not per-chunk regexes: the stream arrives in
 * arbitrary slices, so any marker (`\x02TOOLJSON…\x03`, `\x02RESULT\x03…`,
 * code fences) can straddle a chunk boundary. The May 2026 filter matched
 * markers per chunk and only knew the legacy `[TOOL_START]` form, so once the
 * parser switched to control-char delimiters (2026-05-15) every tool call's
 * JSON payload was read aloud (Ben, 2026-09-24: "it reads tool use").
 *
 * Silent: tool calls (incl. their `description`), tool results, code blocks.
 * Spoken: prose, and `[THINKING]` narration (the per-step bullets written for
 * a human reader — the only progress signal a hands-free listener gets).
 */

type Mode = 'text' | 'tooljson' | 'result' | 'legacy_tool' | 'legacy_result' | 'thinking' | 'code';

const OPENERS: Array<[string, Mode]> = [
  ['\x02TOOLJSON', 'tooljson'],
  ['\x02RESULT\x03', 'result'],
  ['[TOOL_START]', 'legacy_tool'],
  ['[TOOL_RESULT]', 'legacy_result'],
  ['[THINKING]', 'thinking'],
  ['```', 'code'],
];

const CLOSERS: Record<Exclude<Mode, 'text'>, string> = {
  tooljson: '\x03',
  result: '\x02/RESULT\x03',
  legacy_tool: '[TOOL_END]',
  legacy_result: '[/TOOL_RESULT]',
  thinking: '[/THINKING]',
  code: '```',
};

const MAX_OPENER = Math.max(...OPENERS.map(([t]) => t.length));

export class TtsFilter {
  private raw = '';
  private mode: Mode = 'text';

  reset(): void { this.raw = ''; this.mode = 'text'; }

  /** Feed a stream chunk; returns the newly speakable text (may be empty). */
  feed(chunk: string): string { this.raw += chunk; return this.drain(false); }

  /** End of stream: everything still held back becomes speakable (or is dropped if inside a block). */
  finish(): string { const out = this.drain(true); this.reset(); return out; }

  private drain(final: boolean): string {
    let out = '';
    for (;;) {
      if (this.mode !== 'text') {
        const closer = CLOSERS[this.mode];
        const i = this.raw.indexOf(closer);
        if (i < 0) { if (final) this.raw = ''; return out; }
        const inner = this.raw.slice(0, i);
        this.raw = this.raw.slice(i + closer.length);
        if (this.mode === 'thinking' && inner.trim()) out += inner.trim() + '. ';
        this.mode = 'text';
        continue;
      }
      let best = -1; let bestTag = ''; let bestMode: Mode = 'text';
      for (const [tag, mode] of OPENERS) {
        const idx = this.raw.indexOf(tag);
        if (idx >= 0 && (best < 0 || idx < best)) { best = idx; bestTag = tag; bestMode = mode; }
      }
      if (best < 0) {
        const hold = final ? 0 : this.partialMarkerTail();
        out += this.raw.slice(0, this.raw.length - hold);
        this.raw = this.raw.slice(this.raw.length - hold);
        return out;
      }
      out += this.raw.slice(0, best);
      this.raw = this.raw.slice(best + bestTag.length);
      this.mode = bestMode;
    }
  }

  /** Length of a trailing fragment that could be the start of a marker. */
  private partialMarkerTail(): number {
    const max = Math.min(this.raw.length, MAX_OPENER - 1);
    for (let k = max; k >= 1; k--) {
      const suffix = this.raw.slice(this.raw.length - k);
      if (OPENERS.some(([tag]) => tag.startsWith(suffix))) return k;
    }
    return 0;
  }
}

/** Strip tool/narration markup from a whole message (replay path). */
export function stripToolMarkup(text: string): string {
  return text
    .replace(/\x02TOOLJSON[\s\S]*?\x03/g, '')
    .replace(/\x02RESULT\x03[\s\S]*?\x02\/RESULT\x03/g, '')
    .replace(/\[TOOL_START\][\s\S]*?\[TOOL_END\]/g, '')
    .replace(/\[TOOL_RESULT\][\s\S]*?\[\/TOOL_RESULT\]/g, '')
    .replace(/\[\/?THINKING\]/g, '');
}

/**
 * Terminal chrome that can ride along in a stream frame (the bridge relays
 * screen text during the think-gap and on some replace frames): the spinner
 * row ("● Reading 1 file… (esc to interrupt)"), Claude Code tips
 * ("Tip: /install-slack-app to …"), tool rows ("⎿ …"), the prompt, status
 * bar, separators. The display stripper only trims TRAILING chrome lines;
 * for speech we drop any such line wherever it is, and refuse any sentence
 * that still carries a glyph from that vocabulary (Ben, 2026-09-24: heard
 * "circle reading one file install slack app to").
 */
const CHROME_LINE = /^\s*(?:[●⎿↳❯▸⏵✻✽✢·]|─{3,}|Tip:|esc to interrupt|auto-compact|bypass permissions|↓\s*[\d.,]+k?\s*tokens)/i;
const CHROME_SENTENCE = /[●⎿↳❯▸⏵✻✽✢]|\bTip:\s*\/|esc to interrupt|auto-compact|bypass permissions|\(\s*\d+s\s*[·•]|\d+k tokens/i;

export function stripTerminalChrome(text: string): string {
  return text.split('\n').filter((l) => !CHROME_LINE.test(l)).join('\n');
}

export function isChromeSentence(sentence: string): boolean {
  return CHROME_SENTENCE.test(sentence);
}

/**
 * The speakable text of a whole (possibly still streaming) reply: markers and
 * chrome removed, a trailing partial marker held back. Cheap enough to run on
 * every frame, and it makes the speech feed a diff of FILTERED text — so a
 * replace frame that re-serialises a tool call (different spacing/key order
 * inside a silent block) can never shift the diff into the middle of JSON.
 */
export function speakableSoFar(text: string): string {
  const f = new TtsFilter();
  return f.feed(stripTerminalChrome(text));
}
